import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentMultiDocumentVerificationCompleteData,
  type EnrollmentMultiDocumentVerificationCompleteSubmitInput,
  type EnrollmentMultiDocumentVerificationCompleteSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Multi DocumentVerificationComplete data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiDocumentVerificationCompleteData>
 * @example const response = await getEnrollmentMultiDocumentVerificationCompleteData()
 */
export const getEnrollmentMultiDocumentVerificationCompleteData = async (
  context?: DefaultContext,
): Promise<EnrollmentMultiDocumentVerificationCompleteData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiDocumentVerificationCompleteData',
      query: `mutation getEnrollmentMultiDocumentVerificationCompleteData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiDocumentVerificationCompleteData }
  return response.submitEnrollment
}

export const useGetEnrollmentMultiDocumentVerificationCompleteData = createQueryHook<
  undefined,
  EnrollmentMultiDocumentVerificationCompleteData
>(getEnrollmentMultiDocumentVerificationCompleteData)

/**
 * Submit Enrollment Multi DocumentVerificationComplete data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiDocumentVerificationCompleteSubmitOutput>
 * @example const response = await submitEnrollmentMultiDocumentVerificationCompleteData();
 */
export const submitEnrollmentMultiDocumentVerificationCompleteData = async (
  enrollmentDocumentVerificationCompleteSubmitInput: EnrollmentMultiDocumentVerificationCompleteSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiDocumentVerificationCompleteSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiDocumentVerificationCompleteData',
      query: `mutation submitEnrollmentMultiDocumentVerificationCompleteData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentDocumentVerificationCompleteSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiDocumentVerificationCompleteSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentMultiDocumentVerificationCompleteData = createMutationHook<
  EnrollmentMultiDocumentVerificationCompleteSubmitInput,
  EnrollmentMultiDocumentVerificationCompleteSubmitOutput
>(submitEnrollmentMultiDocumentVerificationCompleteData)
